import type { ReportErrorContext } from '../logs/Sentry';
import { fnv1a32 } from '../logs/capgoConsoleRules';
import { lastCapgoReason, type CapgoRingEntry } from './capgoConsoleRing';
import { classifyCapgoReason, isContentReason, type CapgoReason } from './capgoReason';
import { describeUpdateFailure } from './failureDetail';

const MAX_DETAIL = 200;
const HTTP_RANGE_NOT_SATISFIABLE = 416;

export const resolveAttemptFailure = (
  error: unknown,
  lines: CapgoRingEntry[],
): { reason: CapgoReason; failureDetail: string } => {
  const detail = describeUpdateFailure(error);
  const reason = classifyCapgoReason(detail.replace(/\s+/g, ' '));
  const noReason = reason.reasonClass === 'android_no_reason';
  if (!noReason && reason.reasonClass !== 'unclassified') return { reason, failureDetail: detail };
  // The aggregate "Download failed with status" line ends every Android failure and carries no reason.
  for (let i = lines.length - 1; i >= 0; i--) {
    const fromLine = lastCapgoReason([lines[i]]);
    if (fromLine === undefined || fromLine.reasonClass === 'android_no_reason') continue;
    // An unclassified rejection only yields to a ring line that classifies; an unclassified one adds nothing.
    if (noReason || fromLine.reasonClass !== 'unclassified') {
      return { reason: fromLine, failureDetail: lines[i].line.slice(0, MAX_DETAIL) };
    }
  }
  return { reason, failureDetail: detail };
};

export const buildAttemptContext = (input: {
  attempt: number;
  updateMode: string;
  toVersion: string;
  reason: CapgoReason;
  lines: CapgoRingEntry[];
}): ReportErrorContext => {
  const { attempt, updateMode, toVersion, reason, lines } = input;
  const perVersion =
    isContentReason(reason.reasonClass) && reason.httpStatus !== HTTP_RANGE_NOT_SATISFIABLE;
  const fingerprint = perVersion
    ? ['ota-attempt-failed', reason.reasonClass, toVersion]
    : ['ota-attempt-failed', reason.reasonClass];
  // The per-file reason line is dropped from the console, so this event is the only page for a new failure kind.
  if (reason.reasonClass === 'unclassified') fingerprint.push(`h:${fnv1a32(reason.reasonRaw)}`);
  return {
    fingerprint,
    tags: {
      ota_attempt: attempt > 2 ? '10+' : String(attempt),
      ota_update_mode: updateMode,
      ota_to_version: toVersion,
      capgo_reason: reason.reasonClass,
    },
    extra: { capgo_lines: lines },
  };
};
